// Task state management hook
// Extracted from App.js for maintainability
// Now with server sync for cross-device support

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  isValidDate,
  calculateAutoStatus,
  matchesPriorityFilter,
  matchesContextFilters,
  isTaskBlocked,
  getBlockedTasks
} from '../utils/taskHelpers';
import { sanitizeTask, cleanString } from '../utils/sanitize';
import { ACTIONS, CONTEXT_OPTIONS } from '../constants';
import { getTasks, syncTasks } from '../utils/tasksApi';

export default function useTasks(capacity, viewMode, statusFilter, showStarredOnly, getToken, isAuthenticated = false) {
  // ============ STATE ============

  const [tasks, setTasks] = useState([]);
  const [focusIndex, setFocusIndex] = useState(0);
  const [contextOptions, setContextOptions] = useState({
    project: [],
    release: [],
    workflow: []
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);

  // Track if initial load is done to prevent overwriting server data
  const initialLoadDone = useRef(false);
  // Track pending sync to debounce
  const syncTimeoutRef = useRef(null);
  // Store serverUpdatedAt in ref to avoid triggering effect loops
  const serverUpdatedAtRef = useRef(null);
  // Always holds the latest tasks for the debounced save callback
  const latestTasksRef = useRef(tasks);

  // ============ SERVER SYNC ============

  // Safe wrapper for getToken that handles Kinde not being ready
  // Only attempt if user is authenticated
  const safeGetToken = useCallback(async () => {
    // Don't even try if not authenticated - Kinde throws internally if not ready
    if (!isAuthenticated) return null;
    if (!getToken || typeof getToken !== 'function') return null;
    try {
      return await getToken();
    } catch (err) {
      // Kinde throws if not fully initialized yet - this is expected on first render
      // Don't log as error since it's normal during app startup
      return null;
    }
  }, [getToken, isAuthenticated]);

  // Fetch tasks from server
  const fetchFromServer = useCallback(async () => {
    try {
      const token = await safeGetToken();
      if (!token) return null;

      const result = await getTasks(token);
      if (result.success) {
        return {
          tasks: result.tasks || [],
          updatedAt: result.updatedAt
        };
      }
    } catch (err) {
      console.error('Failed to fetch tasks from server:', err);
      setSyncError(err?.message || 'Failed to fetch tasks');
    }
    return null;
  }, [safeGetToken]);

  // Save tasks to server (debounced)
  // Using ref for serverUpdatedAt to keep this callback stable
  const saveToServer = useCallback(async (tasksToSave) => {
    // Update ref so debounced callback always has latest tasks
    latestTasksRef.current = tasksToSave;

    // Clear any pending sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Debounce sync by 1 second to batch rapid changes
    syncTimeoutRef.current = setTimeout(async () => {
      setIsSyncing(true);
      setSyncError(null);

      try {
        const token = await safeGetToken();
        if (!token) {
          setIsSyncing(false);
          return;
        }

        // Use ref to always save the most recent tasks, not stale closure value
        const result = await syncTasks(token, latestTasksRef.current, serverUpdatedAtRef.current);
        if (result.success) {
          serverUpdatedAtRef.current = result.updatedAt;
          if (result.hadConflict) {
            console.warn('Task sync had conflict - server had newer data');
            // For now, our changes win (last write wins)
            // Future: could implement merge logic here
          }
        }
      } catch (err) {
        console.error('Failed to sync tasks to server:', err);
        setSyncError(err?.message || 'Failed to sync tasks');
      } finally {
        setIsSyncing(false);
      }
    }, 1000);
  }, [safeGetToken]); // Removed serverUpdatedAt from deps - using ref instead

  // ============ EFFECTS ============

  // Initial load: try server first, fall back to localStorage
  useEffect(() => {
    const loadTasks = async () => {
      // Try server first if authenticated
      const serverData = await fetchFromServer();

      if (serverData && serverData.tasks.length > 0) {
        // Server has tasks - use them
        setTasks(serverData.tasks);
        serverUpdatedAtRef.current = serverData.updatedAt;
        // Also update localStorage as cache
        localStorage.setItem('mabel-tasks', JSON.stringify(serverData.tasks));
        initialLoadDone.current = true;
        return;
      }

      // Fall back to localStorage
      const saved = localStorage.getItem('mabel-tasks');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setTasks(parsed);
            // If we have local tasks but server is empty, sync them up
            if (isAuthenticated && (!serverData || serverData.tasks.length === 0)) {
              saveToServer(parsed);
            }
          }
        } catch (e) {
          console.error('Failed to load tasks:', e);
        }
      }
      initialLoadDone.current = true;
    };

    loadTasks();
  }, [fetchFromServer, isAuthenticated, saveToServer]);

  // Save to localStorage and server whenever tasks change
  useEffect(() => {
    // Don't save until initial load is complete
    if (!initialLoadDone.current) return;

    // Always save to localStorage as fallback/cache
    localStorage.setItem('mabel-tasks', JSON.stringify(tasks));

    // Sync to server
    saveToServer(tasks);
  }, [tasks, saveToServer]);

  // Extract unique context options from tasks
  useEffect(() => {
    const uniqueProjects = [...new Set(tasks.map(t => t.project).filter(Boolean))];
    const uniqueReleases = [...new Set(tasks.map(t => t.release).filter(Boolean))];
    const uniqueWorkflows = [...new Set(tasks.map(t => t.workflow).filter(Boolean))];
    
    setContextOptions({
      project: uniqueProjects,
      release: uniqueReleases,
      workflow: uniqueWorkflows
    });
  }, [tasks]);

  // ============ COMPUTED VALUES ============

  const filteredTasks = tasks.filter(t => {
    // Apply status filters in both Plan mode and Hub's TasksView
    if (viewMode === 'plan' || viewMode === 'hub') {
      if (statusFilter === 'active' && t.done) return false;
      if (statusFilter === 'done' && !t.done) return false;
      if (statusFilter === 'unblocked' && (t.done || isTaskBlocked(t, tasks))) return false;
      if (statusFilter === 'blocked' && !isTaskBlocked(t, tasks)) return false;
    }
    
    if (viewMode === 'plan' && showStarredOnly && !t.starred) return false;

    if (!matchesPriorityFilter(t, capacity.priority)) return false;
    if (!matchesContextFilters(t, capacity)) return false;
    
    return true;
  });

  const currentTask = filteredTasks[focusIndex];

  // Keep focusIndex in bounds
  useEffect(() => {
    if (filteredTasks.length > 0) {
      if (focusIndex >= filteredTasks.length) {
        setFocusIndex(Math.max(0, filteredTasks.length - 1));
      }
    } else {
      setFocusIndex(0);
    }
  }, [filteredTasks.length, focusIndex]);

  // ============ HELPER FUNCTIONS ============

  const addCustomOption = (field, value) => {
    if (!value || value.trim() === '') return;
    const trimmedValue = value.trim();
    
    setContextOptions(prev => {
      if (prev[field].includes(trimmedValue)) return prev;
      return {
        ...prev,
        [field]: [...prev[field], trimmedValue]
      };
    });
  };

  // ============ TASK HANDLERS ============

  const addTask = (title) => {
    const cleanedTitle = cleanString(title);
    if (!cleanedTitle) return;

    const newTask = sanitizeTask({
      id: crypto.randomUUID(),
      title: cleanedTitle,
      priority: capacity.priority.length > 0 && capacity.priority[0] !== 'Scheduled' ? capacity.priority[0] : 'Unprioritized',
      area: capacity.area.length > 0 ? capacity.area[0] : null,
      project: capacity.project.length > 0 ? capacity.project[0] : null,
      release: capacity.release.length > 0 ? capacity.release[0] : null,
      workflow: capacity.workflow.length > 0 ? capacity.workflow[0] : null,
      timeOfDay: capacity.timeOfDay.length > 0 ? capacity.timeOfDay[0] : null,
      physical: capacity.physical.length > 0 ? capacity.physical[0] : null,
      mental: capacity.mental.length > 0 ? capacity.mental[0] : null,
      social: capacity.social.length > 0 ? capacity.social[0] : null,
      deadline: null,
      starred: false,
      notes: '',
      done: false,
      blockedBy: [],
      blocks: []
    });

    setTasks(prev => [...prev, newTask]);
  };

  // Add a full task object (used by Mabel function calling)
  const addFullTask = (taskData) => {
    if (!taskData || !taskData.title) return;

    const newTask = sanitizeTask({
      id: taskData.id || crypto.randomUUID(),
      title: cleanString(taskData.title),
      priority: taskData.priority || 'Now',
      area: taskData.area || null,
      project: taskData.project || null,
      release: taskData.release || null,
      workflow: taskData.workflow || null,
      timeOfDay: taskData.timeOfDay || null,
      physical: taskData.physical || null,
      mental: taskData.mental || null,
      social: taskData.social || null,
      deadline: taskData.deadline || null,
      starred: taskData.starred || false,
      notes: taskData.notes || '',
      done: taskData.done || false,
      blockedBy: taskData.blockedBy || [],
      blocks: taskData.blocks || []
    });

    setTasks(prev => [...prev, newTask]);
  };

  const markDone = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) {
      console.error('Task not found:', taskId);
      return;
    }

    const blockedTasks = getBlockedTasks(task, tasks);
    if (blockedTasks.length > 0) {
      const blockedTitles = blockedTasks.map(t => t.title).join(', ');
      if (!window.confirm(`This task is blocking: ${blockedTitles}. Mark as done anyway?`)) {
        return;
      }
    }

    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, done: true } : t));
  };

  const markUndone = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) {
      console.error('Task not found:', taskId);
      return;
    }

    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, done: false } : t));
  };

  const skipTask = () => {
    if (filteredTasks.length === 0) return;
    const nextIndex = (focusIndex + 1) % filteredTasks.length;
    setFocusIndex(nextIndex);
  };

  const moveTask = (taskId, newPriority) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, priority: newPriority } : t));
  };

  const deleteTask = (taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
  };

  const updateTask = (taskId, updates) => {
    if (updates.deadline !== undefined && updates.deadline && !isValidDate(updates.deadline)) {
      alert('Invalid date format. Please enter a valid date.');
      return;
    }
    
    const sanitizedUpdates = sanitizeTask(updates);
    
    if (sanitizedUpdates.project) addCustomOption('project', sanitizedUpdates.project);
    if (sanitizedUpdates.release) addCustomOption('release', sanitizedUpdates.release);
    if (sanitizedUpdates.workflow) addCustomOption('workflow', sanitizedUpdates.workflow);
    
    setTasks(prev => prev.map(t => {
      if (t.id === taskId) {
        const updatedTask = { ...t, ...sanitizedUpdates };
        if (sanitizedUpdates.deadline !== undefined && !updatedTask.starred) {
          updatedTask.priority = calculateAutoStatus(sanitizedUpdates.deadline);
        }
        return updatedTask;
      }
      return t;
    }));
  };

  const toggleStar = (taskId) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, starred: !t.starred } : t));
  };

  const clearAllTasks = () => {
    setTasks([]);
  };

  // ============ IMPORT HANDLER ============

  const importTasks = (importText, replace = false) => {
    if (!importText.trim()) {
      alert('Please paste your JSON data first.');
      return false;
    }

    try {
      const parsed = JSON.parse(importText);
      
      let tasksToImport = [];
      let capacityToRestore = null;
      let contextOptionsToRestore = null;
      
      if (Array.isArray(parsed)) {
        tasksToImport = parsed;
      } else if (parsed.tasks) {
        tasksToImport = parsed.tasks;
        capacityToRestore = parsed.capacity;
        contextOptionsToRestore = parsed.contextOptions;
      } else {
        alert('Invalid JSON format. Expected an array of tasks or an object with a "tasks" property.');
        return false;
      }
      
      const validTasks = tasksToImport
        .filter(t => t && typeof t === 'object' && typeof t.title === 'string')
        .map(t => ({
          // Preserve existing IDs (number or string) on import; otherwise mint a UUID.
          id: (typeof t.id === 'number' || typeof t.id === 'string') && t.id ? t.id : crypto.randomUUID(),
          title: t.title.trim() || 'Untitled Task',
          priority: ACTIONS.includes(t.priority) ? t.priority : (ACTIONS.includes(t.action) ? t.action : 'Unprioritized'),
          area: CONTEXT_OPTIONS.area.includes(t.area) ? t.area : null,
          project: typeof t.project === 'string' ? t.project : null,
          release: typeof t.release === 'string' ? t.release : null,
          workflow: typeof t.workflow === 'string' ? t.workflow : null,
          timeOfDay: CONTEXT_OPTIONS.timeOfDay.includes(t.timeOfDay) ? t.timeOfDay : null,
          physical: CONTEXT_OPTIONS.physical.includes(t.physical) ? t.physical : null,
          mental: CONTEXT_OPTIONS.mental.includes(t.mental) ? t.mental : null,
          social: CONTEXT_OPTIONS.social.includes(t.social) ? t.social : null,
          deadline: (typeof t.deadline === 'string' && isValidDate(t.deadline)) ? t.deadline : null,
          starred: t.starred === true,
          notes: typeof t.notes === 'string' ? t.notes : '',
          done: t.done === true,
          blockedBy: Array.isArray(t.blockedBy) ? t.blockedBy : [],
          blocks: Array.isArray(t.blocks) ? t.blocks : []
        }));
      
      if (validTasks.length === 0) {
        alert('No valid tasks found in JSON.');
        return false;
      }
      
      if (replace) {
        setTasks(validTasks);
        alert(`Successfully replaced all tasks with ${validTasks.length} task${validTasks.length === 1 ? '' : 's'}!`);
      } else {
        setTasks(prev => [...prev, ...validTasks]);
        alert(`Successfully added ${validTasks.length} task${validTasks.length === 1 ? '' : 's'}!`);
      }
      
      if (contextOptionsToRestore) {
        setContextOptions(contextOptionsToRestore);
      }
      
      // Return restored capacity for App.js to handle
      return { success: true, capacity: capacityToRestore };
    } catch (error) {
      console.error('Import error:', error);
      alert('Error parsing JSON: ' + error.message);
      return false;
    }
  };

  // ============ RETURN ============

  return {
    // State
    tasks,
    filteredTasks,
    currentTask,
    focusIndex,
    setFocusIndex,
    contextOptions,

    // Sync status
    isSyncing,
    syncError,

    // Handlers
    addTask,
    addFullTask,  // For Mabel function calling
    markDone,
    markUndone,   // For marking tasks as not done
    skipTask,
    moveTask,
    deleteTask,
    updateTask,
    toggleStar,
    clearAllTasks,
    importTasks
  };
}
